<template>
  <header class="flex">
    <router-link class="logo" to="/">
      <img
        class="logo-image my-auto"
        src="../../assets/img/LR_logo2.svg"
        alt="Logo"
        style="width:2.4375rem; height: 2.625rem"
      />
      <span class="logo-title leading-5 italic">LIVE RUSSIA</span>
    </router-link>

    <div v-if="!maxLG" class="desktop-menu">
      <nav class="header-nav">
        <ul class="header-links">
          <li v-for="(item, i) in items" :key="i">
            <component :is="item.tag" v-bind="item.attrs" class="nav-link italic">
              {{ item.title }}
            </component>
          </li>
          <li>
            <router-link to="/donate" class="header-donate">
              {{ t('header_donate') }}
              <span class="donate-flame" aria-hidden="true"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 2s.8 4-2.7 7.1C8.6 7.5 9 5.4 9 5.4S4 9.6 4 14a8 8 0 0 0 16 0c0-5-6.5-12-6.5-12Zm-1.4 18a3.5 3.5 0 0 1-3.5-3.5c0-1.7 1.3-3.2 2.5-4.3-.1 1.7 1 2.5 1.8 3.1.7-.8 1-1.6 1-1.6s1.7 1.7 1.7 2.8a3.5 3.5 0 0 1-3.5 3.5Z"/></svg></span>
            </router-link>
          </li>
        </ul>
      </nav>

      <LanguageDropdown
        id="header-language"
        v-model="selectedLang"
        :languages="languages"
      />
    </div>

    <!-- Burger Menu Button for Mobile -->
    <button v-else class="burger" @click="openModal"></button>

    <teleport to="body">
      <Modal class="mobile-menu-modal" :isOpenValue="modal" :is-click-outside="true">
        <div class="mobile-menu-top">
          <router-link class="logo" to="/">
            <img class="logo-image my-auto" src="../../assets/img/LR_logo2.svg" alt="Logo" />
            <i class="logo-title leading-5">LIVE RUSSIA</i>
          </router-link>
          <button class="close" @click="openModal"></button>
        </div>
        <div class="mobile-menu-content">
          <div class="mobile-menu-items">
            <nav>
              <ul class="mobile-menu-links">
                <li v-for="(item, i) in items" :key="i">
                  <component :is="item.tag" v-bind="item.attrs" class="nav-link" @click="modal = false">
                    {{ item.title }}
                  </component>
                </li>
                <li>
                  <router-link to="/donate" class="nav-link mobile-donate-link" @click="modal = false">{{ t('footer_donate') }}<span class="donate-flame" aria-hidden="true"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 2s.8 4-2.7 7.1C8.6 7.5 9 5.4 9 5.4S4 9.6 4 14a8 8 0 0 0 16 0c0-5-6.5-12-6.5-12Zm-1.4 18a3.5 3.5 0 0 1-3.5-3.5c0-1.7 1.3-3.2 2.5-4.3-.1 1.7 1 2.5 1.8 3.1.7-.8 1-1.6 1-1.6s1.7 1.7 1.7 2.8a3.5 3.5 0 0 1-3.5 3.5Z"/></svg></span></router-link>
                </li>
                <li>
                  <router-link to="/recovery" class="nav-link" @click="modal = false">{{ t('header_recovery') }}</router-link>
                </li>
              </ul>
            </nav>
            <button-accent
              link="/play"
              :title="t('home_start')"
              class="mobile-play-button"
              @click="modal = false"
            />
            <social class="mobile-menu-social" />

            <LanguageDropdown
              id="mobile-language"
              v-model="selectedLang"
              :languages="languages"
              class="mobile-language"
              inline
            />
          </div>
        </div>
      </Modal>
    </teleport>
  </header>
</template>

<script>
import Social from '../Social.vue'
import Modal from '../Modal.vue'
import ButtonAccent from '../ButtonAccent.vue'
import LanguageDropdown from './LanguageDropdown.vue'
import { useI18n } from 'vue-i18n'
import { inject, onMounted, computed } from 'vue'

export default {
  components: { Social, Modal, ButtonAccent, LanguageDropdown },
  setup () {
    const maxLG = inject('scrLarge')
    const modal = inject('modalOpenHeader')
    const { t, locale } = useI18n()

    // Переводы для навигации — обновляются при смене языка
    const items = computed(() => {
      return [
        {
          title: t('footer_main'),
          tag: 'router-link',
          attrs: { to: '/' }
        },
        {
          title: 'Wiki',
          tag: 'a',
          attrs: { href: 'https://wiki.liverussia.online', target: '_blank', rel: 'noopener noreferrer' }
        },
        {
          title: t('footer_forum'),
          tag: 'a',
          attrs: { href: '//forum.liverussia.online/index.php', target: '_blank' }
        },
        {
          title: t('footer_partner'),
          tag: 'a',
          attrs: { href: '//partner.liverussia.online', target: '_blank' }
        }
      ]
    })

    const languages = [
      { code: 'en', label: 'gb', name: 'English' },
      { code: 'ru', label: 'ru', name: 'Русский' },
      { code: 'kk', label: 'kz', name: 'Қазақша' },
      { code: 'de', label: 'de', name: 'Deutsch' },
      { code: 'fr', label: 'fr', name: 'Français' },
      // { code: 'pt', label: 'pt' },
      { code: 'bz', label: 'br', name: 'Português' },
      { code: 'es', label: 'es', name: 'Español' }
    ]

    // Синхронизированный selectedLang с locale
    const selectedLang = computed({
      get: () => locale.value,
      set: (lang) => {
        locale.value = lang
        localStorage.setItem('lang', lang)
      }
    })

    const openModal = () => {
      modal.value = !modal.value
    }

    onMounted(() => {
      const storedLang = localStorage.getItem('lang')
      if (storedLang && storedLang !== locale.value) {
        locale.value = storedLang
      }
    })

    return {
      t,
      items,
      maxLG,
      modal,
      openModal,
      languages,
      selectedLang
    }
  }
}
</script>

<style scoped>
header > .logo {
  flex-shrink: 0;
}
header > .logo .logo-image {
  transition: transform 200ms ease;
}
@media (hover: hover) {
  header > .logo:hover .logo-image {
    transform: scale(1.15);
  }
}
@media (prefers-reduced-motion: reduce) {
  header > .logo .logo-image {
    transition: none;
  }
}
.desktop-menu { display: contents; }
.header-links {
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
  font-size: 16px;
}
.header-donate {
  flex-shrink: 0;
  white-space: nowrap;
  gap: 8px;
  padding: 0 10px;
  font-size: 15px;
  font-family: var(--font-ui);
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 10px;
}
.header {
  @apply relative z-upper mt-0 w-full
  max-w-8xl mx-auto p-[0.625rem]
  max-lg:absolute max-lg:max-w-[96%] max-lg:self-center max-md:mt-6;
}
.homeHeader {
  @apply relative z-upper mt-0 w-full
  max-w-8xl mx-auto p-[0.625rem]
  max-lg:absolute max-lg:max-w-[96%] max-lg:self-center
  max-md:max-w-full
  max-md:mt-0;
}
.header-button {
  @apply bg-transparent px-4 p-[0.625rem] border border-opacity-20
  border-BASE_BUTTON rounded-md text-base/[1.125rem]
  text-BASE_BUTTON font-extrabold uppercase
  hover:bg-BASE_BUTTON hover:text-black transition-colors;
}
.header-link {
  @apply font-black uppercase opacity-70 hover:opacity-100 text-white transition-opacity;
}
.burger-menu {
  @apply absolute inset-0 flex flex-col h-full;
}
.burger {
  @apply absolute w-[1.875rem] h-4 self-center right-5 active:opacity-60;
  background: url('@/assets/img/buttons/burger.svg') center no-repeat;
  background-size: contain;
}
@media (min-width: 1025px) {
  .header,
  .homeHeader {
    width: 90%;
    max-width: none;
    min-width: 0;
    margin-top: 24px;
    margin-bottom: 24px;
    display: grid;
    grid-template-columns: minmax(200px, 1fr) minmax(0, max-content) minmax(200px, 1fr);
    align-items: center;
    column-gap: clamp(12px, 1.5vw, 24px);
    padding: 12px 0;
  }
  header > .logo {
    justify-self: start;
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-size: 21px;
  }
  header > .logo .logo-image {
    width: 48px !important;
    height: 52.5px !important;
  }
  header > .logo .logo-title {
    max-width: 97.5px;
    line-height: 1.1;
  }
  .header-nav { grid-column: 2; min-width: 0; }
  .header-links {
    justify-content: center;
    flex-wrap: wrap;
    gap: 12px 24px;
    font-size: clamp(15px, 1.375vw, 17.5px);
  }
  .header-links .nav-link,
  .header-donate {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    box-sizing: border-box;
    font-size: inherit;
    font-weight: 700;
    font-style: normal;
    line-height: 1;
  }
  .desktop-menu > .language-dropdown { grid-column: 3; justify-self: end; }
}

.mobile-menu-modal {
  overflow-y: auto;
  overscroll-behavior: contain;
}
.mobile-menu-top {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 88px;
  padding: 24px;
  flex-shrink: 0;
}
.mobile-menu-content {
  padding: 24px 20px max(32px, env(safe-area-inset-bottom));
}
.mobile-menu-items {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: min(100%, 420px);
  margin-inline: auto;
}
.mobile-menu-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.mobile-menu-links .nav-link {
  display: block;
  padding: 6px 0;
  color: #fff;
  opacity: 1;
  font-size: 18px;
  line-height: 1.4;
  text-align: center;
}
.mobile-menu-links .nav-link:hover { color: #ffcd00; }
.mobile-menu-links .mobile-donate-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  background: #ef3340;
  color: #fff;
  transition: background-color 150ms;
}
.mobile-menu-links .mobile-donate-link:hover {
  background: #d92532;
  color: #fff;
}
.header-donate {
  background: #ffc700;
  color: #111;
  transition: box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1);
}
.header-donate:hover {
  box-shadow: 0 0 1rem .2rem #ffc700;
  color: #111;
}
.header-donate:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
.donate-flame {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  color: currentColor;
  line-height: 1;
}
.donate-flame svg {
  width: 22px;
  height: 22px;
}
.mobile-play-button.button {
  font-weight: 900;
  font-size: 1.5rem;
  line-height: 1.1;
  padding: 0.75rem 1.5rem;
  min-height: 4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  white-space: normal;
}
.mobile-menu-social {
  width: auto;
  gap: 12px;
}
.mobile-language {
  width: min(100%, 280px);
}
</style>

<style>
.close {
  @apply absolute w-[1.125rem] h-[1.125rem] self-center right-8 active:opacity-60;
  background: url('@/assets/img/buttons/close.svg');
}
</style>
